import PropTypes from 'prop-types';
import React from 'react';
import {uniqueId} from 'underscore';

// eslint-disable-next-line max-len
const pathA = 'M17.87 19.5H14.1l-.65 2.13h-2.93l4.18-11.38h2.57l4.2 11.38h-2.94l-.66-2.13zm-3.1-2.12h2.44L16 13.44l-1.2 3.94z';
// eslint-disable-next-line max-len
const pathB = 'M12 21.63V10.24h4.08c.7 0 1.34.06 1.9.2.55.12 1.02.3 1.4.57.4.26.68.6.88.98.2.4.3.86.3 1.4 0 .25-.02.5-.08.74-.06.25-.15.47-.28.7-.12.2-.3.4-.5.56-.2.17-.44.3-.73.42.34.1.62.22.86.38.23.17.42.36.57.58.14.22.25.46.3.7.08.26.1.52.1.78 0 1.12-.35 1.97-1.07 2.53-.72.55-1.78.82-3.2.82H12zm2.74-4.84v2.72h1.8c.27 0 .5-.04.7-.1.2-.07.36-.17.48-.28.12-.12.2-.26.27-.4.05-.17.08-.34.08-.52 0-.22-.03-.42-.08-.6-.05-.17-.14-.32-.25-.44-.12-.13-.26-.22-.45-.3-.18-.06-.4-.1-.66-.1h-1.9zm0-1.8h1.43c.6 0 1.03-.12 1.28-.34.26-.2.38-.52.38-.94 0-.48-.13-.83-.4-1.04-.3-.2-.73-.3-1.35-.3h-1.34v2.6z';

const PudoIcon = ({color = '#616161', pulse = false, type}) => {
  const labelShadowId = uniqueId('labelShadow');
  const baseShadowId = uniqueId('baseShadow');
  const stemGradientId = uniqueId('stemGradient');
  const stemShadowId = uniqueId('stemShadow');
  const stemShape = (
    <path d="M17 31.97V62c0 .55-.45 1-1 1s-1-.45-1-1V31.97c.33.02.66.03 1 .03.34 0 .67 0 1-.03z" />
  );
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="112" height="120" viewBox="-32 0 112 120">
      <defs>
        {/* Label shadow */}
        <filter
          id={labelShadowId}
          width="200%"
          height="200%"
          x="-50%"
          y="-25%"
          filterUnits="objectBoundingBox"
        >
          <feOffset dy="8" in="SourceAlpha" result="offset1" />
          <feGaussianBlur stdDeviation="4" in="offset1" result="blur1" />
          <feColorMatrix
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.24 0"
            in="blur1"
            result="matrix1"
          />
          <feMerge>
            <feMergeNode in="matrix1" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {/* Base circle shadow */}
        <filter
          id={baseShadowId}
          width="156.2%"
          height="156.2%"
          x="-28.1%"
          y="-21.9%"
          filterUnits="objectBoundingBox"
        >
          <feOffset dy="1" in="SourceAlpha" result="offset1" />
          <feGaussianBlur stdDeviation="1" in="offset1" result="blur1" />
          <feColorMatrix
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"
            in="blur1"
            result="matrix1"
          />
          <feMerge>
            <feMergeNode in="matrix1" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {/* Stem shading */}
        <linearGradient id={stemGradientId} x1="50%" x2="50%" y1="0%" y2="100%">
          <stop stopColor="#F7F7F7" offset="0%" />
          <stop stopColor="#FFF" offset="100%" />
        </linearGradient>
        {stemShape}
        {/* Stem shadow */}
        <filter
          id={stemShadowId}
          width="750%"
          height="141.9%"
          x="-325%"
          y="-17.7%"
          filterUnits="objectBoundingBox"
        >
          <feOffset dy="1" in="SourceAlpha" result="offset1" />
          <feGaussianBlur stdDeviation="2" in="offset1" result="blur1" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0" in="blur1" />
        </filter>
      </defs>
      <g fill="none" fillRule="evenodd">
        <g transform="translate(8)">
          {/* Draw a pulse if pulse is True */}
          {pulse && (
            <circle cx="16" cy="63" r="0" fill={color}>
              <animate
                attributeName="opacity"
                from="0.4"
                to="0"
                dur="1s"
                repeatCount="indefinite"
              />
              <animate attributeName="r" from="0" to="56" dur="1s" repeatCount="indefinite" />
            </circle>
          )}
          {/* Draw an A/B label if the type is specified */}
          {type && (
            <g filter={`url(#${labelShadowId})`}>
              <circle cx="16" cy="16" r="14.5" fill={color} stroke="#FFF" strokeWidth="3" />
              <path fill="#FFF" fillRule="nonzero" d={type === 'pickup' ? pathA : pathB} />
            </g>
          )}
          {/* Draw a base circle */}
          <g
            fill={color}
            stroke="#FFF"
            strokeWidth="3"
            filter={`url(#${baseShadowId})`}
            transform="translate(8 55)"
          >
            <circle cx="8" cy="8" r="7.5" />
          </g>
          {/* Draw a stem if the type is specified */}
          {type && (
            <g>
              <g fill="#000" filter={`url(#${stemShadowId})`}>
                {stemShape}
              </g>
              <g fill={`url(#${stemGradientId})`}>
                {stemShape}
              </g>
            </g>
          )}
          {/* Draw a white dot in the center of the base if the type is specified */}
          {type && <circle cx="16" cy="63" r="2" fill="#FFF" />}
        </g>
      </g>
    </svg>
  );
};

PudoIcon.propTypes = {
  color: PropTypes.string,
  pulse: PropTypes.bool,
  type: PropTypes.oneOf(['pickup', 'dropoff']),
};

export default PudoIcon;



// WEBPACK FOOTER //
// ./src/common/components/PudoOverlayMarker/PudoIcon.js